import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import Navbar from "./components/Navbar";
import MobileBottomNav from "./components/MobileBottomNav";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import AuthCallback from "./pages/AuthCallback";
import GoogleCallback from "./pages/GoogleCallback";
import Browse from "./pages/Browse";
import BrowseEquipment from "./pages/BrowseEquipment";
import SystemDetail from "./pages/SystemDetail";
import EquipmentDetail from "./pages/EquipmentDetail";
import BookingForm from "./pages/BookingForm";
import EquipmentBookingForm from "./pages/EquipmentBookingForm";
import Cart from "./pages/Cart";
import Payment from "./pages/Payment";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import MyBookings from "./pages/MyBookings";
import AdminDashboard from "./pages/AdminDashboard";
import BusinessProfile from "./pages/BusinessProfile";
import BusinessDashboard from "./pages/BusinessDashboard";
import BecomeVendor from "./pages/BecomeVendor";
import BusinessEmailVerify from "./pages/BusinessEmailVerify";
import { Toaster } from "./components/ui/toaster";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

function AppContent() {
  const location = useLocation();
  const { user, logout } = useAuth();

  // Check for OAuth callback with session_id in hash
  const hasSessionId = location.hash.includes('session_id=');

  // Pages where navbar should be hidden
  const hideNavbarPages = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email'];
  const showNavbar = !hideNavbarPages.some(page => location.pathname.startsWith(page)) && !hasSessionId;

  // If we have session_id in URL, render AuthCallback
  if (hasSessionId) {
    return <AuthCallback />;
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <ScrollToTop />
      {showNavbar && <Navbar user={user} onLogout={logout} />}
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/auth/google/callback" element={<GoogleCallback />} />
        <Route path="/dashboard" element={<Navigate to="/browse" replace />} />
        <Route path="/browse" element={<Browse />} />
        <Route path="/equipment" element={<BrowseEquipment />} />
        <Route path="/business/:businessId" element={<BusinessProfile />} />
        <Route path="/system/:id" element={<SystemDetail />} />
        <Route path="/equipment/:id" element={<EquipmentDetail />} />
        <Route 
          path="/booking/:systemId" 
          element={
            <ProtectedRoute>
              <BookingForm />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/book/:systemId" 
          element={
            <ProtectedRoute>
              <BookingForm />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/equipment-booking/:equipmentId" 
          element={
            <ProtectedRoute>
              <EquipmentBookingForm />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/equipment/:equipmentId/book" 
          element={
            <ProtectedRoute>
              <EquipmentBookingForm />
            </ProtectedRoute>
          } 
        />
        <Route path="/cart" element={<Cart />} />
        <Route 
          path="/payment" 
          element={
            <ProtectedRoute>
              <Payment />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/payment/success" 
          element={
            <ProtectedRoute>
              <PaymentSuccess />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/payment/cancel" 
          element={
            <ProtectedRoute>
              <PaymentCancel />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/my-bookings" 
          element={
            <ProtectedRoute>
              <MyBookings />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin" 
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/business-dashboard" 
          element={
            <ProtectedRoute>
              <BusinessDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/business/dashboard" 
          element={
            <ProtectedRoute>
              <BusinessDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/become-vendor" 
          element={
            <ProtectedRoute>
              <BecomeVendor />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/business/verify-email" 
          element={
            <ProtectedRoute>
              <BusinessEmailVerify />
            </ProtectedRoute>
          } 
        />
      </Routes>
      <Toaster />
      {showNavbar && <MobileBottomNav />}
      {/* Spacer for mobile bottom nav */}
      {showNavbar && <div className="md:hidden h-16" />}
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <AppContent />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
